import { useCallback, useState } from 'react';
import { message } from 'antd';

import { MenuSections } from '../../../types/menu-sections';
import { useInitializeBanner } from '../../../hooks/banners/creation/use-initialize-banner';
import { useUploadMedia } from '../../../hooks/media/use-upload-media';
import { getHandlerForTemplate } from '../../../handlers';
import { useClientId } from '../../../hooks/use-client-id';
import { useBanners } from '../../../context/banners';
import { useModal } from '../../../context/modal';
import { useTemplateConfig } from '../../../hooks/banners/use-template-config';

import { useEditorData } from './use-editor-data';
import { useSaveEditorBanner } from './use-save-editor-banner';
import { useSaveProgress } from './use-save-progress';
import {
  autoSwitchToFirstInvalidResolution,
  collectEditorModels,
  prepareBannerData,
  renderPreviews,
  uploadPreviews
} from '../utils/save-banner-pipeline';

interface UseSubmitEditorBannerParams {
  onNavigate: (section: MenuSections) => void;
}

export function useSubmitEditorBanner({ onNavigate }: UseSubmitEditorBannerParams) {
  const [isSaving, setIsSaving] = useState(false);
  const { saveProgress, report, reset: resetProgress } = useSaveProgress();

  const {
    creativityType,
    contentId,
    formData,
    resolutions,
    resolveResolutionJson,
    validateForm,
    activeResolution,
    setActiveResolutionId,
    editorRef
  } = useEditorData();

  const clientId = useClientId();
  const clientHandler = getHandlerForTemplate(creativityType);
  const template = useTemplateConfig(creativityType);

  const { initializeBanner } = useInitializeBanner();
  const { uploadVideoDirect, uploadPosterDirect } = useUploadMedia();
  const { saveEditorBanner } = useSaveEditorBanner();
  const { refreshAllBanners } = useBanners();
  const { openAsset } = useModal();

  const handleSaveToLibrary = useCallback(async () => {
    const { isValid, errors } = validateForm();
    if (!isValid) {
      autoSwitchToFirstInvalidResolution(errors, template, activeResolution.id, setActiveResolutionId);
      message.error('Por favor, corrige los errores del formulario');
      return;
    }

    setIsSaving(true);
    report('Inicializando...', 0);

    try {
      // Reopened piece: reuse its id so save updates it; new piece: mint a fresh one.
      const bannerId = contentId ?? (await initializeBanner());

      const { processed, finalBannerType, cleanedConfig, folder } = await prepareBannerData({
        formData,
        clientHandler,
        creativityType,
        clientId,
        bannerId,
        uploadVideoDirect,
        onProgress: report
      });

      const rendered = await renderPreviews({
        resolutions,
        resolveResolutionJson,
        onProgress: report
      });

      const mediaFiles = await uploadPreviews({
        rendered,
        clientHandler,
        processed,
        finalBannerType,
        clientId,
        bannerId,
        uploadPosterDirect,
        onProgress: report
      });

      const editorModels = await collectEditorModels({
        resolutions,
        activeResolutionId: activeResolution.id,
        resolveResolutionJson,
        getActiveModel: () => editorRef.current?.getModel() ?? null
      });

      report('Ya casi...', 80);
      const result = await saveEditorBanner(
        bannerId,
        finalBannerType,
        cleanedConfig,
        (processed.title as string) ?? '',
        mediaFiles,
        editorModels,
        folder
      );

      if (result) {
        report('¡Listo!', 100);
        refreshAllBanners();
        setTimeout(() => {
          resetProgress();
          openAsset(result.banner.bannerId);
          onNavigate('dashboard');
        }, 1500);
      } else {
        resetProgress();
        message.error('Error al guardar');
      }
    } catch (error) {
      console.error('Error saving editor:', error);
      resetProgress();
      message.error('Error al guardar');
    } finally {
      setIsSaving(false);
    }
  }, [
    validateForm,
    formData,
    creativityType,
    contentId,
    clientHandler,
    clientId,
    resolutions,
    resolveResolutionJson,
    editorRef,
    initializeBanner,
    uploadVideoDirect,
    uploadPosterDirect,
    saveEditorBanner,
    refreshAllBanners,
    openAsset,
    onNavigate,
    template,
    activeResolution.id,
    setActiveResolutionId,
    report,
    resetProgress
  ]);

  return { handleSaveToLibrary, isSaving, saveProgress };
}
